import {
  DefaultError,
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import Pagination from 'common/types/Pagination';
import { useCallback, useMemo } from 'react';

import { CustomerJob } from 'domain/entities/CustomerJob';
import { JobRepository, SearchCustomerJobsParams } from 'domain/repositories/IJobRepository';
import { PaginatedResult } from 'domain/types/PaginatedResult';
import { Entity } from 'domain/types/Entity';
import { useHookInjection } from 'application/hooks';

export const QUERY_PREFIX = 'CUSTOMER_JOBS';

export const useCaseSearchCustomerJobs = (
  params: Omit<SearchCustomerJobsParams, 'offset'> | null,
) => {
  const qClient = useQueryClient();

  const { searchCustomerJobs } = useHookInjection<JobRepository>(JobRepository);

  const initialPageParam: Pagination = useMemo(
    () => ({
      limit: params?.limit ?? 10,
      offset: 0,
    }),
    [params?.limit],
  );

  const { data, isLoading, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery<
      PaginatedResult<Entity<CustomerJob>>,
      DefaultError,
      InfiniteData<PaginatedResult<Entity<CustomerJob>>>,
      [string, typeof params],
      Pagination
    >({
      queryKey: [QUERY_PREFIX, params],
      enabled: !!params,
      queryFn: ({ queryKey, pageParam }) => searchCustomerJobs({ ...pageParam, ...queryKey[1] }),
      getNextPageParam: (lastPage, _pages, lastPageParam) => {
        if (lastPageParam.offset + lastPageParam.limit < lastPage.total) {
          return {
            ...lastPageParam,
            offset: lastPageParam.offset + lastPageParam.limit,
          };
        }

        return null;
      },
      initialPageParam,
      maxPages: 100,
      staleTime: 10_000,
      refetchInterval: 30_000,
      placeholderData: {
        pages: [{ items: [], total: 0 }],
        pageParams: [initialPageParam],
      },
    });

  const allPages = useMemo(
    () => data?.pages.map((page) => page.items).flat(1) ?? [],
    [data?.pages],
  );

  const refetch = useCallback(
    () => qClient.invalidateQueries({ queryKey: [QUERY_PREFIX] }),
    [qClient],
  );

  return {
    pages: data?.pages ?? [],
    allPages,
    total: data?.pages[0]?.total ?? 0,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  };
};
